import React from "react";
import { cn } from "@/lib/utils";

interface FoxCircle {
  x: number;
  y: number;
  size: number;
  color: string;
  delay: number;
}

const FOX_ORANGE = "hsl(25, 95%, 55%)";
const FOX_ORANGE_DARK = "hsl(25, 90%, 42%)";
const FOX_ORANGE_LIGHT = "hsl(35, 95%, 65%)";
const FOX_CREAM = "hsl(35, 90%, 85%)";
const HOODIE_GREEN = "hsl(165, 55%, 38%)";
const HOODIE_GREEN_DARK = "hsl(165, 55%, 30%)";
const HOODIE_V = "hsl(200, 90%, 55%)";
const PANTS_BROWN = "hsl(25, 50%, 35%)";
const PANTS_BROWN_LIGHT = "hsl(25, 45%, 42%)";
const SHOE_WHITE = "hsl(60, 20%, 92%)";
const SHOE_SOLE = "hsl(60, 10%, 75%)";
const NOSE_COLOR = "hsl(15, 40%, 25%)";
const EYE_COLOR = "hsl(200, 70%, 35%)";
const PHONE_DARK = "hsl(220, 15%, 25%)";
const PHONE_SCREEN = "hsl(200, 80%, 60%)";
const TAIL_TIP = "hsl(25, 90%, 42%)";

const BASE_SIZE = 7;

/**
 * Full fox mascot silhouette in a 200x350 viewbox.
 * Standing fox in hoodie holding phone, matching the brand hero.
 */
function generateFoxCircles(): FoxCircle[] {
  const circles: FoxCircle[] = [];
  let idx = 0;

  const add = (points: { x: number; y: number }[], color: string, size = BASE_SIZE) => {
    for (const p of points) {
      circles.push({ x: p.x, y: p.y, size, color, delay: idx * 0.018 });
      idx++;
    }
  };

  // === EARS ===
  // Left ear (pointy triangle)
  add([
    { x: 62, y: 8 }, { x: 56, y: 18 }, { x: 68, y: 18 },
    { x: 50, y: 28 }, { x: 62, y: 28 }, { x: 74, y: 28 },
    { x: 56, y: 38 }, { x: 68, y: 38 },
  ], FOX_ORANGE);
  // Left ear inner
  add([{ x: 62, y: 20 }, { x: 58, y: 30 }, { x: 66, y: 30 }], FOX_ORANGE_LIGHT, 5);

  // Right ear
  add([
    { x: 138, y: 8 }, { x: 132, y: 18 }, { x: 144, y: 18 },
    { x: 126, y: 28 }, { x: 138, y: 28 }, { x: 150, y: 28 },
    { x: 132, y: 38 }, { x: 144, y: 38 },
  ], FOX_ORANGE);
  // Right ear inner
  add([{ x: 138, y: 20 }, { x: 134, y: 30 }, { x: 142, y: 30 }], FOX_ORANGE_LIGHT, 5);

  // === HEAD ===
  // Top of head
  add([
    { x: 72, y: 42 }, { x: 82, y: 38 }, { x: 92, y: 36 }, { x: 100, y: 35 },
    { x: 108, y: 36 }, { x: 118, y: 38 }, { x: 128, y: 42 },
  ], FOX_ORANGE);
  // Head sides & fill
  add([
    { x: 65, y: 50 }, { x: 60, y: 60 }, { x: 58, y: 72 },
    { x: 135, y: 50 }, { x: 140, y: 60 }, { x: 142, y: 72 },
  ], FOX_ORANGE);
  // Head fill
  add([
    { x: 75, y: 48 }, { x: 85, y: 45 }, { x: 95, y: 44 }, { x: 105, y: 44 },
    { x: 115, y: 45 }, { x: 125, y: 48 },
    { x: 70, y: 56 }, { x: 80, y: 52 }, { x: 90, y: 50 }, { x: 100, y: 49 },
    { x: 110, y: 50 }, { x: 120, y: 52 }, { x: 130, y: 56 },
    { x: 68, y: 65 }, { x: 132, y: 65 },
  ], FOX_ORANGE);

  // === EYES ===
  add([{ x: 80, y: 62 }, { x: 88, y: 60 }], "white", 8);
  add([{ x: 112, y: 62 }, { x: 120, y: 60 }], "white", 8);
  add([{ x: 84, y: 62 }], EYE_COLOR, 5);
  add([{ x: 116, y: 62 }], EYE_COLOR, 5);
  add([{ x: 84, y: 62 }], NOSE_COLOR, 3);
  add([{ x: 116, y: 62 }], NOSE_COLOR, 3);

  // === MUZZLE / MOUTH ===
  add([
    { x: 85, y: 70 }, { x: 92, y: 68 }, { x: 100, y: 67 }, { x: 108, y: 68 }, { x: 115, y: 70 },
    { x: 88, y: 76 }, { x: 96, y: 74 }, { x: 100, y: 73 }, { x: 104, y: 74 }, { x: 112, y: 76 },
    { x: 92, y: 82 }, { x: 100, y: 84 }, { x: 108, y: 82 },
  ], FOX_CREAM, 6);
  // Nose
  add([{ x: 100, y: 72 }], NOSE_COLOR, 5);
  // Smile
  add([{ x: 96, y: 79 }, { x: 100, y: 80 }, { x: 104, y: 79 }], FOX_ORANGE_DARK, 3);

  // Cheeks / jaw
  add([
    { x: 62, y: 78 }, { x: 68, y: 84 }, { x: 78, y: 86 },
    { x: 138, y: 78 }, { x: 132, y: 84 }, { x: 122, y: 86 },
    { x: 88, y: 88 }, { x: 100, y: 90 }, { x: 112, y: 88 },
  ], FOX_ORANGE);

  // === HOODIE / TORSO ===
  // Neck
  add([
    { x: 88, y: 95 }, { x: 100, y: 95 }, { x: 112, y: 95 },
  ], FOX_ORANGE);

  // Hoodie top (green)
  add([
    { x: 70, y: 102 }, { x: 80, y: 100 }, { x: 90, y: 98 }, { x: 100, y: 98 },
    { x: 110, y: 98 }, { x: 120, y: 100 }, { x: 130, y: 102 },
  ], HOODIE_GREEN);
  // Hoodie body fill
  add([
    { x: 65, y: 112 }, { x: 75, y: 108 }, { x: 85, y: 106 }, { x: 95, y: 105 },
    { x: 105, y: 105 }, { x: 115, y: 106 }, { x: 125, y: 108 }, { x: 135, y: 112 },
    { x: 62, y: 122 }, { x: 72, y: 118 }, { x: 82, y: 115 }, { x: 92, y: 113 },
    { x: 102, y: 113 }, { x: 112, y: 115 }, { x: 122, y: 118 }, { x: 132, y: 120 },
    { x: 60, y: 132 }, { x: 70, y: 128 }, { x: 80, y: 125 }, { x: 90, y: 123 },
    { x: 100, y: 122 }, { x: 110, y: 123 }, { x: 120, y: 125 }, { x: 130, y: 128 },
    { x: 60, y: 142 }, { x: 70, y: 138 }, { x: 80, y: 135 }, { x: 90, y: 133 },
    { x: 100, y: 132 }, { x: 110, y: 133 }, { x: 120, y: 135 }, { x: 130, y: 138 },
    { x: 62, y: 150 }, { x: 72, y: 148 }, { x: 82, y: 145 }, { x: 92, y: 143 },
    { x: 102, y: 143 }, { x: 112, y: 145 }, { x: 122, y: 148 }, { x: 130, y: 150 },
  ], HOODIE_GREEN);
  // Hoodie darker sides
  add([
    { x: 58, y: 118 }, { x: 56, y: 128 }, { x: 55, y: 138 }, { x: 56, y: 148 },
    { x: 138, y: 116 }, { x: 140, y: 126 },
  ], HOODIE_GREEN_DARK);

  // "V" logo on hoodie
  add([
    { x: 95, y: 118 }, { x: 100, y: 128 }, { x: 105, y: 118 },
    { x: 97, y: 123 }, { x: 103, y: 123 },
  ], HOODIE_V, 4);

  // Hoodie pocket line
  add([
    { x: 78, y: 148 }, { x: 88, y: 150 }, { x: 100, y: 151 }, { x: 112, y: 150 }, { x: 122, y: 148 },
  ], HOODIE_GREEN_DARK, 4);

  // === LEFT ARM (fox's left, our right — holding phone) ===
  add([
    { x: 140, y: 110 }, { x: 145, y: 118 }, { x: 148, y: 126 },
    { x: 150, y: 134 }, { x: 152, y: 142 },
  ], HOODIE_GREEN);
  // Paw/hand
  add([
    { x: 154, y: 148 }, { x: 152, y: 154 }, { x: 156, y: 154 },
  ], FOX_ORANGE, 5);

  // Phone in hand
  add([
    { x: 158, y: 135 }, { x: 158, y: 142 }, { x: 158, y: 149 },
    { x: 164, y: 135 }, { x: 164, y: 142 }, { x: 164, y: 149 },
  ], PHONE_DARK, 5);
  add([
    { x: 161, y: 140 }, { x: 161, y: 146 },
  ], PHONE_SCREEN, 4);

  // === RIGHT ARM (fox's right, our left — down) ===
  add([
    { x: 52, y: 112 }, { x: 48, y: 122 }, { x: 45, y: 132 },
    { x: 44, y: 142 }, { x: 44, y: 152 },
  ], HOODIE_GREEN);
  // Paw
  add([
    { x: 44, y: 158 }, { x: 40, y: 160 }, { x: 48, y: 160 },
  ], FOX_ORANGE, 5);

  // === HOODIE BOTTOM ===
  add([
    { x: 65, y: 158 }, { x: 75, y: 156 }, { x: 85, y: 155 }, { x: 95, y: 155 },
    { x: 105, y: 155 }, { x: 115, y: 156 }, { x: 125, y: 158 },
  ], HOODIE_GREEN);

  // === TAIL (big, curving to the right behind body) ===
  add([
    { x: 135, y: 148 }, { x: 142, y: 145 }, { x: 150, y: 140 },
    { x: 158, y: 132 }, { x: 164, y: 122 }, { x: 168, y: 112 },
    { x: 170, y: 102 }, { x: 168, y: 92 }, { x: 164, y: 84 },
  ], FOX_ORANGE, 9);
  // Tail fill (thicker)
  add([
    { x: 140, y: 142 }, { x: 148, y: 136 }, { x: 155, y: 128 },
    { x: 160, y: 118 }, { x: 163, y: 108 }, { x: 165, y: 98 },
  ], FOX_ORANGE, 8);
  // Tail tip (darker)
  add([
    { x: 166, y: 88 }, { x: 162, y: 80 }, { x: 158, y: 76 },
  ], TAIL_TIP, 8);
  // Tail white tip
  add([
    { x: 160, y: 78 }, { x: 156, y: 74 },
  ], FOX_CREAM, 7);

  // === PANTS / LEGS ===
  // Left leg (our right)
  add([
    { x: 110, y: 162 }, { x: 115, y: 170 }, { x: 118, y: 180 },
    { x: 120, y: 190 }, { x: 120, y: 200 }, { x: 120, y: 210 },
    { x: 120, y: 220 },
  ], PANTS_BROWN);
  // Right side fill
  add([
    { x: 105, y: 165 }, { x: 125, y: 168 },
    { x: 108, y: 175 }, { x: 126, y: 178 },
    { x: 112, y: 185 }, { x: 126, y: 188 },
    { x: 114, y: 195 }, { x: 126, y: 198 },
    { x: 114, y: 205 }, { x: 126, y: 208 },
    { x: 114, y: 215 }, { x: 126, y: 218 },
  ], PANTS_BROWN_LIGHT);

  // Right leg (our left)
  add([
    { x: 85, y: 162 }, { x: 82, y: 170 }, { x: 80, y: 180 },
    { x: 78, y: 190 }, { x: 78, y: 200 }, { x: 78, y: 210 },
    { x: 78, y: 220 },
  ], PANTS_BROWN);
  add([
    { x: 90, y: 165 }, { x: 72, y: 168 },
    { x: 88, y: 175 }, { x: 72, y: 178 },
    { x: 86, y: 185 }, { x: 72, y: 188 },
    { x: 84, y: 195 }, { x: 72, y: 198 },
    { x: 84, y: 205 }, { x: 72, y: 208 },
    { x: 84, y: 215 }, { x: 72, y: 218 },
  ], PANTS_BROWN_LIGHT);

  // Crotch area / pants center
  add([
    { x: 95, y: 162 }, { x: 100, y: 165 },
    { x: 95, y: 172 }, { x: 100, y: 174 },
  ], PANTS_BROWN);

  // === SHOES ===
  // Left shoe (our right)
  add([
    { x: 114, y: 226 }, { x: 120, y: 228 }, { x: 126, y: 228 },
    { x: 132, y: 226 }, { x: 120, y: 234 }, { x: 128, y: 234 },
  ], SHOE_WHITE, 6);
  add([
    { x: 118, y: 238 }, { x: 126, y: 238 }, { x: 134, y: 236 },
  ], SHOE_SOLE, 5);

  // Right shoe (our left)
  add([
    { x: 72, y: 226 }, { x: 78, y: 228 }, { x: 84, y: 228 },
    { x: 68, y: 226 }, { x: 74, y: 234 }, { x: 80, y: 234 },
  ], SHOE_WHITE, 6);
  add([
    { x: 66, y: 236 }, { x: 74, y: 238 }, { x: 82, y: 238 },
  ], SHOE_SOLE, 5);

  return circles;
}

const FOX_CIRCLES = generateFoxCircles();
const VIEWBOX_WIDTH = 200;
const VIEWBOX_HEIGHT = 250;
const ANIMATION_DURATION_S = 1.4;

interface FoxBouncingLoaderProps {
  size?: number;
  className?: string;
}

const FoxBouncingLoader: React.FC<FoxBouncingLoaderProps> = ({
  size = 300,
  className,
}) => {
  const scaleX = size / VIEWBOX_WIDTH;
  const scaleY = (size * (VIEWBOX_HEIGHT / VIEWBOX_WIDTH)) / VIEWBOX_HEIGHT;
  const height = size * (VIEWBOX_HEIGHT / VIEWBOX_WIDTH);

  return (
    <div
      className={cn("relative", className)}
      style={{ width: size, height }}
    >
      {FOX_CIRCLES.map((circle, i) => (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            width: circle.size * scaleX,
            height: circle.size * scaleY,
            left: circle.x * scaleX - (circle.size * scaleX) / 2,
            top: circle.y * scaleY - (circle.size * scaleY) / 2,
            backgroundColor: circle.color,
            animation: `fox-bounce ${ANIMATION_DURATION_S}s ease-in-out infinite`,
            animationDelay: `${circle.delay}s`,
          }}
        />
      ))}

      <style>{`
        @keyframes fox-bounce {
          0%, 80%, 100% {
            transform: scale(0);
            opacity: 0;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default FoxBouncingLoader;
